import useAsync from "@AppBuilderShared/hooks/misc/useAsync";
import { IAppBuilderSettingsSession } from "@AppBuilderShared/types/shapediver/appbuilder";
import { useEffect, useMemo, useState } from "react";
import { recordCeruleoTelemetryEvent } from "../instruments/ceruleoTelemetry";
import { z } from "zod";
import {
  buildSelectedScanPayload,
  buildSelectedScanParameterValues,
  parseSelectedScanFromEnvelope,
  parseSelectedScanFromUrl,
  publishSelectedScan,
  resolveRuntimeSelectedScan,
  RuntimeSelectedScan,
} from "./selectedScanRuntime";

const bootstrapResponseSchema = z.object({
  tokenId: z.string().uuid(),
  viewerSessionId: z.string().uuid(),
  mode: z.enum(["hosted", "embedded"]),
  orgId: z.string().uuid(),
  appId: z.string().uuid(),
  deploymentId: z.string().uuid(),
  deployment: z.object({
    shapeDiverModelId: z.string().nullable(),
    shapeDiverModelViewUrl: z.string().url().nullable(),
  }),
  activeConfig: z
    .object({
      shapeDiver: z
        .object({
          modelId: z.string().min(1),
          modelViewUrl: z.string().url(),
        })
        .optional(),
      appv: z
        .object({
          ticket: z.string().min(1),
          modelViewUrl: z.string().url(),
        })
        .optional(),
    })
    .nullable(),
  configError: z.string().nullable(),
  selectedScan: z
    .object({
      scanSessionId: z.string().uuid(),
      accessUrl: z.string().url(),
      assetType: z.string().min(1).max(64).nullable(),
    })
    .nullable()
    .optional(),
  appvTheme: z
    .object({
      layoutMode: z.enum(["sidebar", "overlay"]).default("overlay"),
      lightDarkDefault: z.enum(["light", "dark"]).default("light"),
      colors: z.record(z.string()).default({}),
      fonts: z.record(z.string()).default({}),
      logoAssetUrl: z.string().url().nullable().optional(),
      loadingIconAssetUrl: z.string().url().nullable().optional(),
      cameraOffsetX: z.number().nullable().optional(),
    })
    .optional(),
});

export type CeruleoAppvTheme = NonNullable<
  z.infer<typeof bootstrapResponseSchema>["appvTheme"]
>;

type BootstrapResponse = z.infer<typeof bootstrapResponseSchema>;

type BootstrapResult = {
  session: IAppBuilderSettingsSession;
  tokenId: string;
  viewerSessionId: string;
  orgId: string;
  appId: string;
  deploymentId: string;
  mode: "hosted" | "embedded";
  bootstrapPayload: BootstrapResponse;
};

export function useCeruleoHostedSessionBootstrap() {
  const params = useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  );
  const urlSelectedScan = useMemo(
    () => parseSelectedScanFromUrl(params),
    [params],
  );
  const [eventSelectedScan, setEventSelectedScan] =
    useState<RuntimeSelectedScan | null>(null);

  const tokenId =
    params.get("ceruleoToken") ??
    params.get("token");
  const requestedMode = params.get("mode");
  const shouldBootstrap = Boolean(tokenId);
  const apiBaseUrl = resolveApiBaseUrl(params);

  const { loading, error, value } = useAsync(
    async () => {
      if (!shouldBootstrap || !tokenId) {
        return undefined;
      }

      const requestUrl = new URL("/api/ceruleo/viewer/bootstrap", apiBaseUrl);
      requestUrl.searchParams.set("token", tokenId);

      const response = await fetch(requestUrl.toString(), {
        method: "GET",
        credentials: "omit",
      });

      const rawPayload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(
          extractBootstrapErrorMessage(rawPayload) ??
            `Viewer bootstrap failed (${response.status})`,
        );
      }

      const parsed = bootstrapResponseSchema.safeParse(rawPayload);
      if (!parsed.success) {
        throw new Error("Viewer bootstrap response is invalid");
      }

      if (requestedMode && requestedMode !== parsed.data.mode) {
        console.warn(
          `Ceruleo viewer mode mismatch (url=${requestedMode}, bootstrap=${parsed.data.mode})`,
        );
      }

      if (parsed.data.configError) {
        console.warn(
          `Ceruleo active config issue: ${parsed.data.configError}`,
        );
      }

      return {
        session: resolveSessionFromBootstrap(parsed.data),
        tokenId: parsed.data.tokenId,
        viewerSessionId: parsed.data.viewerSessionId,
        orgId: parsed.data.orgId,
        appId: parsed.data.appId,
        deploymentId: parsed.data.deploymentId,
        mode: parsed.data.mode,
        bootstrapPayload: parsed.data,
      } satisfies BootstrapResult;
    },
    [shouldBootstrap, tokenId, requestedMode, apiBaseUrl],
    {
      onSuccess: (result) => {
        if (!result) {
          return;
        }
        recordCeruleoTelemetryEvent("viewer_loaded", {
          mode: result.mode,
          token_id: result.tokenId,
          viewer_session_id: result.viewerSessionId,
          org_id: result.orgId,
          app_id: result.appId,
          deployment_id: result.deploymentId,
        });
      },
      onError: (bootstrapError) => {
        const message =
          bootstrapError instanceof Error
            ? bootstrapError.message
            : String(bootstrapError);
        recordCeruleoTelemetryEvent("error", {
          code: "bootstrap_error",
          message,
        });
      },
    },
  );

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const selectedScan = parseSelectedScanFromEnvelope(event.data);
      if (selectedScan) {
        setEventSelectedScan(selectedScan);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const selectedScan = useMemo(
    () =>
      resolveRuntimeSelectedScan({
        bootstrap: value?.bootstrapPayload,
        envelope: eventSelectedScan
          ? {
              id: "runtime-selected-scan",
              protocolVersion: "1.0",
              event: "scan.selected",
              source: "host",
              sentAt: new Date().toISOString(),
              payload: {
                scanSessionId: eventSelectedScan.scanSessionId,
                asset: {
                  assetType: eventSelectedScan.assetType,
                  accessUrl: eventSelectedScan.accessUrl,
                },
              },
            }
          : undefined,
        url: params,
      }) ?? urlSelectedScan,
    [eventSelectedScan, params, urlSelectedScan, value?.bootstrapPayload],
  );
  const selectedScanPayload = useMemo(
    () => buildSelectedScanPayload(selectedScan),
    [selectedScan],
  );
  const session = useMemo(() => {
    if (!value?.session) {
      return undefined;
    }

    const selectedScanParameterValues =
      buildSelectedScanParameterValues(selectedScanPayload);

    return {
      ...value.session,
      initialParameterValues: {
        ...(value.session.initialParameterValues ?? {}),
        ...selectedScanParameterValues,
      },
    } satisfies IAppBuilderSettingsSession;
  }, [selectedScanPayload, value?.session]);

  useEffect(() => {
    publishSelectedScan(selectedScan ?? null);
  }, [selectedScan]);

  return {
    bootstrapping: shouldBootstrap,
    loading: shouldBootstrap ? loading : false,
    error: shouldBootstrap ? error : undefined,
    session,
    tokenId: value?.tokenId,
    mode: value?.mode,
    selectedScan,
    selectedScanPayload,
    appvTheme: value?.bootstrapPayload.appvTheme,
  };
}

function resolveSessionFromBootstrap(
  response: BootstrapResponse,
): IAppBuilderSettingsSession {
  const configAppv = response.activeConfig?.appv;
  const appvTicket = configAppv?.ticket?.trim();
  const appvModelViewUrl = configAppv?.modelViewUrl?.trim();
  if (appvTicket && appvModelViewUrl) {
    return {
      id: "default",
      ticket: appvTicket,
      modelViewUrl: appvModelViewUrl,
      platformUrl: new URL(appvModelViewUrl).origin,
    };
  }

  const configShapeDiver = response.activeConfig?.shapeDiver;
  const modelId = configShapeDiver?.modelId ?? response.deployment.shapeDiverModelId;
  const modelViewUrl =
    configShapeDiver?.modelViewUrl ?? response.deployment.shapeDiverModelViewUrl;

  const slug = modelId ?? extractModelIdentifierFromViewUrl(modelViewUrl);
  if (!slug) {
    throw new Error(
      "Unable to resolve ShapeDiver model identifier from Ceruleo bootstrap response",
    );
  }

  return {
    id: "default",
    slug,
    modelViewUrl: modelViewUrl ?? "",
    platformUrl: modelViewUrl ? new URL(modelViewUrl).origin : undefined,
  };
}

function extractModelIdentifierFromViewUrl(
  modelViewUrl: string | null,
): string | undefined {
  if (!modelViewUrl) {
    return undefined;
  }

  try {
    const url = new URL(modelViewUrl);
    const match = url.pathname.match(/\/models\/([^/?#]+)/i);
    return match?.[1];
  } catch {
    return undefined;
  }
}

function resolveApiBaseUrl(params: URLSearchParams): string {
  const explicit = params.get("ceruleoApiBaseUrl");
  if (explicit) {
    return normalizeApiBaseUrl(explicit);
  }

  if (typeof document !== "undefined" && document.referrer) {
    try {
      return normalizeApiBaseUrl(new URL(document.referrer).origin);
    } catch {
      // fall through
    }
  }

  return normalizeApiBaseUrl(window.location.origin);
}

function extractBootstrapErrorMessage(payload: unknown): string | undefined {
  if (!payload || typeof payload !== "object") {
    return undefined;
  }

  const data = payload as Record<string, unknown>;
  if (typeof data.error === "string" && data.error.length > 0) {
    return data.error;
  }

  if (typeof data.message === "string" && data.message.length > 0) {
    return data.message;
  }

  return undefined;
}

function normalizeApiBaseUrl(value: string): string {
  try {
    const parsed = new URL(value);
    const isLocalHost =
      parsed.hostname === "localhost" ||
      parsed.hostname === "127.0.0.1" ||
      parsed.hostname === "::1";

    if (isLocalHost && parsed.port === "3000") {
      parsed.port = "3001";
    }

    return parsed.origin;
  } catch {
    return value.replace(/\/+$/, "");
  }
}
